import type { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import { useHostname } from "@/lib/ssr";
import { isNewHomeEnabled } from "@/lib/homeFlag";
import SiteFrame from "@/pages/home/SiteFrame";
import { WRAP } from "@/pages/home/ui";

/**
 * The frame for the Photohawk Family app's web pages (/personal/privacy, /personal/terms, /personal/delete-account,
 * /personal/support). They are public and indexable everywhere from 20 October 2026: the App Store listing and the
 * app link to them. Where the new site is on (staging until launch: src/lib/homeFlag.ts) they wear its header and
 * footer on the light ground; where it's off (production) they wear today's site header and footer, like
 * /face-data-policy/family, so they never link to the new site's pages that production doesn't have yet.
 */
const PersonalShell = ({ title, children }: { title: string; children: ReactNode }) => {
  const newSite = isNewHomeEnabled(useHostname());
  const head = (
    <Helmet>
      <title>{title}</title>
    </Helmet>
  );
  if (newSite)
    return (
      <SiteFrame page="app-legal" path="app" mainClassName={`${WRAP} py-[clamp(32px,5vw,64px)]`}>
        {head}
        {children}
      </SiteFrame>
    );
  return (
    <div className="min-h-screen bg-background" data-personal-page="app-legal">
      {head}
      <AppHeader variant="marketing" />
      <main className="container mx-auto max-w-3xl px-4 pb-20 pt-28">{children}</main>
      <Footer />
    </div>
  );
};

export default PersonalShell;

/** Prose styles for long text (the legal texts, the account-deletion and support pages), on the new site's light
 *  ground, in the dashboard's tokens. */
export const PROSE =
  "max-w-[46rem] text-base leading-relaxed text-foreground [&_a]:font-semibold [&_a]:text-[#A6560A] [&_a]:underline [&_blockquote]:my-5 [&_blockquote]:border-l-4 [&_blockquote]:border-[#EA8A23] [&_blockquote]:bg-muted [&_blockquote]:px-4 [&_blockquote]:py-3 [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:text-[0.9em] [&_h1]:mb-4 [&_h1]:font-display [&_h1]:text-[clamp(30px,4vw,44px)] [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:tracking-[-0.03em] [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h3]:mb-2 [&_h3]:mt-6 [&_h3]:text-lg [&_h3]:font-bold [&_hr]:my-8 [&_hr]:border-border [&_li]:my-1.5 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-4 [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_.md-table]:my-5 [&_.md-table]:overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_td]:border [&_td]:border-border [&_td]:bg-background [&_td]:p-2.5 [&_td]:align-top [&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:p-2.5 [&_th]:text-left";

/** The same on today's site (production until the new site launches), in its colours, as on /face-data-policy/family. */
export const LEGACY_PROSE =
  "text-muted-foreground leading-relaxed [&_a]:text-hawk-orange [&_a]:underline [&_blockquote]:my-5 [&_blockquote]:border-l-4 [&_blockquote]:border-hawk-orange [&_blockquote]:bg-muted [&_blockquote]:px-4 [&_blockquote]:py-3 [&_h1]:mb-4 [&_h1]:font-display [&_h1]:text-3xl md:[&_h1]:text-4xl [&_h1]:font-bold [&_h1]:text-foreground [&_h2]:mb-3 [&_h2]:mt-10 [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:mt-6 [&_h3]:font-semibold [&_h3]:text-foreground [&_strong]:text-foreground [&_li]:my-1.5 [&_ol]:my-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:my-4 [&_ul]:my-4 [&_ul]:list-disc [&_ul]:pl-6 [&_hr]:my-8 [&_hr]:border-border [&_.md-table]:my-5 [&_.md-table]:overflow-x-auto [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_td]:border [&_td]:border-border [&_td]:p-2.5 [&_td]:align-top [&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:p-2.5 [&_th]:text-left [&_th]:text-foreground";

/** The prose styles for this deployment's frame. */
export const usePersonalProse = (): string => (isNewHomeEnabled(useHostname()) ? PROSE : LEGACY_PROSE);
